// The boards' filters (the Filters dialog): categories of a row's values, each offering the
// values on the board as options. Within a category the picked options are alternatives (PGL or
// EGL); across categories they all apply (PGL, and in the Midwest).
//
// A port of the ECI mobile app's src/boardFilters.ts. Keep the two in step. No Angular here, so
// Node can test it (boardFilters.test.mjs).

/**
 * @typedef {{key: string, label: string}} FilterValue
 * @typedef {{key: string, title: string, values: (row: object) => FilterValue[],
 *   order?: (a: FilterValue, b: FilterValue) => number}} FilterCategory
 * @typedef {FilterValue & {count: number}} FilterOption
 * @typedef {Object<string, string[]>} FilterSelection The picked option keys, by category key.
 */

const picked = (selection, key) => selection[key] || [];

/** Whether a row passes every category with something picked, leaving out the category `except`. */
export function matchesFilters(row, selection, categories, except) {
  return categories.every(c => {
    const keys = picked(selection, c.key);
    if (c.key === except || keys.length === 0) {
      return true;
    }
    return c.values(row).some(v => keys.includes(v.key));
  });
}

/** The rows that pass the selection. */
export function applyFilters(rows, selection, categories) {
  return rows.filter(row => matchesFilters(row, selection, categories));
}

/**
 * A category's options: every value on the board, each counting the rows it would leave with the
 * other categories' picks (so a count is what picking it shows). A picked value stays listed even
 * when nothing else would leave a row with it, so it can be unpicked.
 * @returns {FilterOption[]}
 */
export function filterOptions(rows, category, selection, categories) {
  const options = new Map();
  rows.forEach(row => {
    const others = matchesFilters(row, selection, categories, category.key);
    // A row counts once per value, however often the value appears in it.
    const seen = new Set();
    category.values(row).forEach(v => {
      if (seen.has(v.key)) {
        return;
      }
      seen.add(v.key);
      const option = options.get(v.key) || {...v, count: 0};
      if (others) {
        option.count++;
      }
      options.set(v.key, option);
    });
  });
  const order = category.order || ((a, b) => a.label.localeCompare(b.label, undefined, {numeric: true}));
  return [...options.values()]
    .filter(o => o.count > 0 || picked(selection, category.key).includes(o.key))
    .sort(order);
}

/** How many options are picked across the categories. */
export const activeFilterCount = selection =>
  Object.values(selection).reduce((n, keys) => n + keys.length, 0);

/** The selection with one option picked or unpicked (a new object; the old one is unchanged). */
export function toggleFilter(selection, category, key) {
  const keys = picked(selection, category);
  const next = keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key];
  const out = {...selection, [category]: next};
  if (next.length === 0) {
    delete out[category];
  }
  return out;
}
